import React, { useState, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useDispatch, useSelector } from "react-redux";
import {
  addDescription,
  deleteDescription,
  editDescription,
  getTodosFromLocalStorage,
  saveStateForUndoRedux,
  toggleDescriptionCompletion,
  undoDescription,
  updateTodoPriority,
  copyDescription,
  pasteDescription,
  setTodoOrderIds,
} from "../redux/todo/todoSlice";
import { FaPlus, FaSave } from "react-icons/fa";
import { FaRegCircleCheck } from "react-icons/fa6";
import { FaRegCircle } from "react-icons/fa";
import { FiMoreVertical } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import {
  create_or_update_todo_order,
  create_todo_thunk,
  delete_todo_thunk,
  edit_todo_thunk,
  update_todo_priority_thunk,
} from "../redux/todo/todoThunk";
import { setFileOrderIds } from "../redux/folder/folderSlice";
import { isMacOS } from "../common/common";

// Context Menu Component
const ContextMenu = ({
  x,
  y,
  onEdit,
  onDelete,
  onClose,
  alignment = "left",
}) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.95 }}
    // className="fixed bg-gray-800 rounded-lg shadow-lg py-1 z-50 min-w-[120px]"
    // style={{ top: y, left: x }}
    style={{
      position: "fixed",
      top: y,
      [alignment]: x,
      zIndex: 1000,
    }}
    className="bg-gray-800/95 shadow-md shadow-gray-700 backdrop-blur-sm text-white  rounded-lg p-1.5 min-w-[180px] border border-gray-700/50"
  >
    <button
      onClick={onEdit}
      className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-gray-700/50 bg-gray-800/95 rounded-md transition-colors"
    >
      Edit
    </button>
    <button
      onClick={onDelete}
      className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-gray-700/50 bg-gray-800/95 rounded-md transition-colors"
    >
      Delete
    </button>
  </motion.div>
);
export default function TodoTab({
  todo,
  userId,
  inputValue,
  onInputChange,
  sidebarInputRef,
  todoInputRef,
  todoEditInputRef,
  tabRenameInputRef,
  setShortCutFileRef,
  isComposing,
  setIsComposing,
}) {
  const token = localStorage.getItem("todoToken");
  const undoStack = useSelector((state) => state.todo.undoStack);
  // const redoStack = useSelector((state) => state.todo.redoStack);
  const descriptions = useSelector((state) => state.todo.descriptions);
  const todoOrderIds = useSelector((state) => state.todo.todoOrderIds);
  const copiedDescriptions = useSelector(
    (state) => state.todo.copiedDescriptions
  );
  const shortcutsKeys = useSelector((state) => state.shortcut.shortcuts);

  const [currentPageTodo, setCurrentPageTodo] = useState([]);
  // const [newDescription, setNewDescription] = useState("");
  const [editingDescId, setEditingDescId] = useState(null);
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  const [editedDescription, setEditedDescription] = useState("");
  const [selectedDescriptions, setSelectedDescriptions] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  // Context menu state
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    descId: null,
    alignment: "left",
  });

  const dispatch = useDispatch();
  const todoContainerRef = useRef(null);

  useEffect(() => {
    dispatch(getTodosFromLocalStorage());
  }, []);

  // const getCurrentPageTodo = (fileId) => {
  //   const filteredDescriptions = descriptions.filter(
  //     (desc) => desc.fileId === fileId
  //   );

  //   try {
  //     const currentTodoOrder = todoOrderIds[fileId] || [];

  //     const sortedDescriptions = currentTodoOrder.length
  //       ? [...filteredDescriptions].sort((a, b) => {
  //           if (a.completed !== b.completed) {
  //             return a.completed - b.completed; // Incomplete (false) first
  //           }
  //           return (
  //             currentTodoOrder.indexOf(a.id) - currentTodoOrder.indexOf(b.id)
  //           );
  //         })
  //       : [...filteredDescriptions].sort((a, b) => {
  //           if (a.completed !== b.completed) {
  //             return a.completed - b.completed; // Incomplete (false) first
  //           }
  //           return b.priority - a.priority; // Higher priority first within the same completion status
  //         });
  //     setCurrentPageTodo(sortedDescriptions);
  //     return;
  //   } catch (error) {
  //     console.log("error", error);
  //     const sortedDescriptions = [...filteredDescriptions].sort((a, b) => {
  //       if (a.completed !== b.completed) {
  //         return a.completed - b.completed; // Incomplete (false) first
  //       }
  //       return b.priority - a.priority; // Higher priority first within the same completion status
  //     });

  //     setCurrentPageTodo(sortedDescriptions);
  //     return;
  //     // return sortedDescriptions
  //   }
  // };

  const getCurrentPageTodo = (fileId) => {
    const filteredDescriptions = descriptions.filter(
      (desc) => desc.fileId === fileId
    );

    try {
      const currentTodoOrder = todoOrderIds[fileId] || [];

      const sortedDescriptions = [...filteredDescriptions].sort((a, b) => {
        // First, sort by completion status
        if (a.completed !== b.completed) {
          return a.completed - b.completed; // Incomplete (false) first
        }

        // Check if items are in todoOrderIds
        const aInOrder = currentTodoOrder.includes(a.id);
        const bInOrder = currentTodoOrder.includes(b.id);

        // If one is in order and other isn't, prioritize the one that's not in order
        if (aInOrder !== bInOrder) {
          return aInOrder ? 1 : -1; // Items not in order come first
        }

        // If both are in todoOrderIds, sort by their order
        if (aInOrder && bInOrder) {
          return (
            currentTodoOrder.indexOf(a.id) - currentTodoOrder.indexOf(b.id)
          );
        }

        // If neither is in todoOrderIds, sort by createdAt
        return new Date(b.createdAt) - new Date(a.createdAt); // Most recent first
      });

      setCurrentPageTodo(sortedDescriptions);
      return;
    } catch (error) {
      console.log("error", error);
      const sortedDescriptions = [...filteredDescriptions].sort((a, b) => {
        if (a.completed !== b.completed) {
          return a.completed - b.completed;
        }
        return new Date(b.createdAt) - new Date(a.createdAt); // Most recent first
      });

      setCurrentPageTodo(sortedDescriptions);
      return;
    }
  };
  useEffect(() => {
    if (todo && descriptions?.length > 0) {
      // const filteredDescriptions = descriptions.filter(
      //   (desc) => desc.fileId === todo.id
      // );
      // const sortedDescriptions = filteredDescriptions.sort((a, b) => {
      //   if (a.completed !== b.completed) {
      //     return a.completed - b.completed; // Incomplete (false) first
      //   }
      //   return b.priority - a.priority; // Higher priority first within the same completion status
      // });
      // setCurrentPageTodo(sortedDescriptions);
      if (!isDragging) {
        // You'll need to add this state
        getCurrentPageTodo(todo.id);
      }
    } else {
      setCurrentPageTodo([]);
    }
  }, [descriptions, todo, todoOrderIds, isDragging]);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Deselect descriptions when clicking outside
      if (
        todoContainerRef.current &&
        !todoContainerRef.current.contains(event.target) &&
        selectedDescriptions.length > 0
      ) {
        setSelectedDescriptions([]);
      }

      // Handle exiting edit mode
      if (editingDescId !== null) {
        // Check if the click was outside the edit input
        if (
          todoEditInputRef.current &&
          !todoEditInputRef.current.contains(event.target)
        ) {
          const currentText = currentPageTodo.find(
            (desc) => desc.id === editingDescId
          )?.text;

          if (editedDescription !== currentText) {
            // Save changes if content was modified
            handleSaveEdit(editingDescId);
          } else {
            // Exit edit mode without saving if no changes
            setEditingDescId(null);
            setEditedDescription("");
          }
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedDescriptions, editingDescId, editedDescription, currentPageTodo]);

  useEffect(() => {
    if (todo && todoInputRef.current) todoInputRef.current.focus();
  }, [todo]);

  useEffect(() => {
    if (editingDescId && todoEditInputRef.current)
      todoEditInputRef.current.focus();
  }, [editingDescId]);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.visible) {
        setContextMenu({ ...contextMenu, visible: false });
      }
    };

    window.addEventListener("click", handleClickOutside);
    return () => window.removeEventListener("click", handleClickOutside);
  }, [contextMenu]);

  const handleAddDescription = () => {
    if (inputValue.trim()) {
      const descriptionWithId = {
        id: uuidv4(),
        text: inputValue,
        completed: false,
        fileId: todo.id,
        userId,
      };

      // Dispatch the description without todoOrder for local state
      // dispatch(addDescription({ description: descriptionWithId }));

      // Create new order array with the new description at the start
      // const newTodoOrder = todoOrderIds[todo.id]
      //   ? [...todoOrderIds[todo.id]]
      //   : currentPageTodo.map((desc) => desc.id);

      // newTodoOrder.splice(0, 0, descriptionWithId.id);
      // dispatch(setTodoOrderIds({ fileId: todo.id, todoIds: newTodoOrder }));
      dispatch(addDescription({ description: descriptionWithId }));

      // Add todoOrder for the API call
      const dataForApi = {
        ...descriptionWithId,
        // todoOrder: newTodoOrder,
      };

      // Send to API
      dispatch(create_todo_thunk({ data: dataForApi, token: token }));

      // Clear input
      onInputChange("");
    }
  };

  const handleDeleteDescription = (descId) => {
    const deletedItems = currentPageTodo.filter((desc) => desc.id === descId); // Find the item being deleted
    saveStateForUndo(deletedItems); // Pass the deleted item to saveStateForUndo
    dispatch(deleteDescription({ descIds: [descId] }));
    dispatch(
      delete_todo_thunk({ descIds: [descId], deleted: true, token: token })
    );
    setContextMenu({ ...contextMenu, visible: false });
  };

  const handleDeleteSelected = () => {
    if (selectedDescriptions.length > 0) {
      const deletedItems = currentPageTodo.filter((desc) =>
        selectedDescriptions.includes(desc.id)
      ); // Find all selected items being deleted
      saveStateForUndo(deletedItems); // Pass the deleted items to saveStateForUndo
      dispatch(
        deleteDescription({
          descIds: selectedDescriptions,
        })
      );
      dispatch(
        delete_todo_thunk({
          descIds: selectedDescriptions,
          deleted: true, // Restore these items
          token: token,
        })
      );
      setSelectedDescriptions([]); // Clear selection after deletion
    }
  };

  const handleDoubleClick = (desc) => {
    if (editingDescId === desc.id) return;
    setEditingDescId(desc.id);
    setEditedDescription(desc.text);
    // Small delay to ensure input is focused after state update
    setTimeout(() => todoEditInputRef.current?.focus(), 0);
  };
  const handleEditDescription = (descId) => {
    setEditingDescId(descId);
    const currentDesc = currentPageTodo.find((desc) => desc.id === descId);
    if (currentDesc) setEditedDescription(currentDesc.text);
    setContextMenu({ ...contextMenu, visible: false });
  };

  const handleSaveEdit = (descId) => {
    // saveStateForUndo();
    const description = descriptions.find((desc) => desc.id === descId);
    if (description.text === editedDescription) {
      setEditingDescId(null);
      setEditedDescription("");
      return;
    }
    let todoData = {
      userId,
      todoId: todo.id,
      descId,
      text: editedDescription,
    };
    dispatch(editDescription(todoData));
    dispatch(edit_todo_thunk({ data: todoData, token: token }));
    setEditingDescId(null);
    setEditedDescription("");
  };

  const handleContextMenu = (e, descId) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      descId,
      position: "absolute",
      top: e.clientY - rect.top,
      left: e.clientX - rect.left,
      alignment: "left",
    });
  };

  const handleIconClick = (e, descId) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setContextMenu({
      visible: true,
      x: rect.left,
      y: rect.bottom,
      descId,
      position: "fixed",
      alignment: "left",
    });
  };

  const handleToggleCompletion = (descId, completed, text) => {
    // saveStateForUndo();
    let todoData = {
      userId,
      todoId: todo.id,
      descId,
      completed,
      text,
    };
    dispatch(toggleDescriptionCompletion(todoData));
    dispatch(edit_todo_thunk({ data: todoData, token: token }));
  };

  const handleSelectDescription = (descId, index, event) => {
    todoInputRef.current?.blur();
    if (event.ctrlKey || event.metaKey) {
      if (selectedDescriptions.includes(descId)) {
        setSelectedDescriptions((prev) => prev.filter((id) => id !== descId));
      } else {
        setSelectedDescriptions((prev) => [...prev, descId]);
      }
      setLastSelectedIndex(index);
    } else if (event.shiftKey && lastSelectedIndex !== null) {
      const start = Math.min(index, lastSelectedIndex);
      const end = Math.max(index, lastSelectedIndex);
      const range = currentPageTodo
        .slice(start, end + 1)
        .map((desc) => desc.id);
      setSelectedDescriptions((prev) => [...new Set([...prev, ...range])]);
    } else {
      setSelectedDescriptions(
        selectedDescriptions.includes(descId) ? [] : [descId]
      );
      setLastSelectedIndex(index);
    }
  };

  const isSelected = (descId) => selectedDescriptions.includes(descId);

  const undoAction = () => {
    if (undoStack.length > 0) {
      const lastDeleted = undoStack[undoStack.length - 1]; // Only track the last deleted items
      const descIds = lastDeleted.map((desc) => desc.id); // Get their IDs
      dispatch(undoDescription());
      dispatch(
        delete_todo_thunk({
          descIds,
          deleted: false, // Restore these items
          token,
        })
      );
    }
  };

  // const redoAction = () => {
  //   if (redoStack.length > 0) {
  //     const lastRestored = redoStack[redoStack.length - 1]; // Only track the last restored items
  //     const descIds = lastRestored.map((desc) => desc.id); // Get their IDs
  //     dispatch(redoDescription());
  //     dispatch(
  //       delete_todo_thunk({
  //         descIds,
  //         deleted: true, // Re-delete these items
  //         token,
  //       })
  //     );
  //   }
  // };

  const saveStateForUndo = (deletedItems) => {
    dispatch(saveStateForUndoRedux({ deletedItems }));
  };

  const handleCopyDescription = () => {
    if (selectedDescriptions.length > 0) {
      const descriptionsToCopy = currentPageTodo.filter((desc) =>
        selectedDescriptions.includes(desc.id)
      );
      if (descriptionsToCopy.length > 0) {
        dispatch(copyDescription({ descriptions: descriptionsToCopy }));
      }
    }
  };

  const handlePasteDescription = () => {
    if (copiedDescriptions.length > 0) {
      const newIds = copiedDescriptions.reduce((acc, desc) => {
        acc[desc.id] = uuidv4();
        return acc;
      }, {});
      dispatch(pasteDescription({ newIds, fileId: todo.id }));
      copiedDescriptions.forEach((desc) => {
        dispatch(
          create_todo_thunk({
            data: { ...desc, id: newIds[desc.id], fileId: todo.id },
            token,
          })
        );
      });
    }
  };

  useEffect(() => {
    const handleGlobalKeyPress = (e) => {
      if (editingDescId) {
        return;
      }
      // Ignore if focus is on an input or contenteditable element
      if (
        document.activeElement === sidebarInputRef?.current ||
        document.activeElement === todoInputRef?.current ||
        document.activeElement === todoEditInputRef?.current ||
        document.activeElement === tabRenameInputRef?.current ||
        document.activeElement === setShortCutFileRef?.current
      ) {
        // Let the input's default Ctrl+A etc behavior work
        if (
          (e.metaKey &&
            ["a", "c", "v", "z", "x"].includes(e.key.toLowerCase())) ||
          e.key === "Backspace" ||
          e.key === "Delete"
        ) {
          return; // Allow default behavior for these shortcuts
        }
      }

      // Ignore if Control or Meta key is pressed
      if (e.ctrlKey || e.metaKey) {
        return;
      }

      // Focus input on alphanumeric key presses
      if (e.key.length === 1 && /^[a-zA-Z0-9\s.,!?-]$/.test(e.key)) {
        // onInputChange(e.key);
        setIsTyping(true);
        todoInputRef.current?.focus();
        setTimeout(() => {
          // todoInputRef.current.value += e.key; // Append key to input field
          onInputChange(todoInputRef.current.value);
        }, 0);
      }
    };

    window.addEventListener("keypress", handleGlobalKeyPress);
    return () => window.removeEventListener("keypress", handleGlobalKeyPress);
  }, [shortcutsKeys, editingDescId]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        document.activeElement === sidebarInputRef?.current ||
        document.activeElement === todoInputRef?.current ||
        document.activeElement === todoEditInputRef?.current ||
        document.activeElement === tabRenameInputRef?.current ||
        document.activeElement === setShortCutFileRef?.current
      ) {
        if (
          (e.metaKey &&
            ["a", "c", "v", "z", "x"].includes(e.key.toLowerCase())) ||
          e.key === "Backspace" ||
          e.key === "Delete"
        ) {
          return; // Allow default behavior for these shortcuts
        }
      }
      // Handle global shortcuts if input is not focused
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        handleDeleteSelected();
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        undoAction();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a") {
        e.preventDefault();
        const allDescriptionIds = currentPageTodo.map((desc) => desc.id);
        setSelectedDescriptions(allDescriptionIds);
      }

      // Let the default behavior happen for input when focused

      // Handle other custom shortcuts
      if (["Control", "Alt", "Shift", "Meta", "Enter"].includes(e.key)) {
        return;
      }

      const modifiers = [];
      if (e.ctrlKey) modifiers.push("Ctrl");
      if (e.altKey) {
        let isMac = isMacOS();
        if (isMac) {
          modifiers.push("Option");
        } else {
          modifiers.push("Alt");
        }
      }
      if (e.shiftKey) modifiers.push("Shift");
      if (e.metaKey) modifiers.push("Cmd");

      let key = e.key;
      if (key === " ") key = "Space";

      if (e.altKey) {
        // Use the base key without converting to uppercase
        key = e.code.replace("Key", "");
      } else if (key.length === 1) {
        key = key.toUpperCase();
      }

      const newKey = [...modifiers, key].join("+");
      let shortcutId;
      shortcutsKeys?.Todo?.forEach((shortcut) => {
        if (shortcut.key === newKey) {
          shortcutId = shortcut.id;
        }
      });
      switch (shortcutId) {
        case "todo-focus":
          e.preventDefault();
          todoInputRef.current?.focus();
          setIsTyping(true);
          break;
        case "todo-copy":
          e.preventDefault();
          handleCopyDescription();
          break;
        case "todo-paste":
          e.preventDefault();
          handlePasteDescription();
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    inputValue,
    selectedDescriptions,
    undoStack,
    // redoStack,
    currentPageTodo,
    shortcutsKeys,
  ]);

  const handleDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    let selectedFile = currentPageTodo[0].fileId;
    const todoIds = currentPageTodo.map((todo) => todo.id);
    const [removed] = todoIds.splice(source.index, 1);
    todoIds.splice(destination.index, 0, removed);
    // Immediately update the local state to prevent flickering
    const filteredDescriptions = descriptions.filter(
      (desc) => desc.fileId === selectedFile
    );

    const sortedDescriptions = [...filteredDescriptions].sort((a, b) => {
      if (a.completed !== b.completed) {
        return a.completed - b.completed;
      }
      return todoIds.indexOf(a.id) - todoIds.indexOf(b.id);
    });

    // Update local state immediately
    setCurrentPageTodo(sortedDescriptions);
    setIsDragging(false);

    // Then update Redux state
    dispatch(setTodoOrderIds({ todoIds, fileId: selectedFile }));
    dispatch(
      create_or_update_todo_order({
        data: { todoOrder: todoIds, fileId: selectedFile },
        token,
      })
    );
  };

  return (
    <div className="bg-gradient-to-br p-6" id="todo-tab">
      <div className="flex items-center mb-4 gap-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          onFocus={() => setIsTyping(true)}
          onBlur={() => setIsTyping(false)}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={() => setIsComposing(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !isComposing) {
              e.preventDefault();
              handleAddDescription(); // Call the function on Enter
            }
          }}
          placeholder="What needs to be done?"
          className="flex-1 p-3 border border-gray-700 rounded-lg bg-gray-800/50 text-white placeholder-gray-400 
                   focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-transparent
                   transition-all duration-300 text-sm"
          ref={todoInputRef}
        />
        <button
          onClick={handleAddDescription}
          className="p-2 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg text-white 
                 shadow-lg flex items-center justify-center transition-all duration-300"
          title="Add Task"
        >
          <FaPlus className="text-base" />
        </button>
      </div>

      {contextMenu.visible && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onEdit={() => handleEditDescription(contextMenu.descId)}
          onDelete={() => handleDeleteDescription(contextMenu.descId)}
          onClose={() => setContextMenu({ ...contextMenu, visible: false })}
          alignment={contextMenu.alignment}
        />
      )}
      <div ref={todoContainerRef}>
        <DragDropContext
          onDragStart={() => setIsDragging(true)}
          onDragEnd={(result) => {
            handleDragEnd(result);
          }}
        >
          <Droppable droppableId="todo-list">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="todo-list  fancy-scrollbar"
              >
                {currentPageTodo.length > 0 &&
                  currentPageTodo
                    .filter((desc) => !desc.completed)
                    .map((desc, index) => (
                      <Draggable
                        key={desc.id}
                        draggableId={desc.id}
                        index={index}
                        isDragDisabled={
                          editingDescId !== null || desc?.completed
                        }
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{
                              ...provided.draggableProps.style,
                              transition: snapshot.isDragging
                                ? provided.draggableProps.style.transition
                                : "none",
                              cursor: "pointer",
                            }}
                            onClick={(e) =>
                              handleSelectDescription(desc.id, index, e)
                            }
                            onDoubleClick={() => handleDoubleClick(desc)}
                            onContextMenu={(e) => handleContextMenu(e, desc.id)}
                            className={`p-4 mb-3 rounded-lg flex justify-between items-center
                            ${
                              isSelected(desc.id)
                                ? "bg-gradient-to-r from-blue-500/20 to-cyan-500/20 "
                                : "bg-gray-900/50 hover:bg-gray-700/50"
                            } 
                            backdrop-blur-sm transition-all duration-300 cursor-pointer
                            shadow-md hover:shadow-lg`}
                          >
                            {editingDescId === desc.id ? (
                              <>
                                <input
                                  type="text"
                                  value={editedDescription}
                                  onChange={(e) =>
                                    setEditedDescription(e.target.value)
                                  }
                                  className="flex-1 p-2 bg-gray-700 rounded-md text-white focus:outline-none 
                                       focus:ring-2 focus:ring-cyan-500 transition-all duration-300 text-sm"
                                  ref={todoEditInputRef}
                                  onKeyDown={(event) => {
                                    if (event.key === "Enter") {
                                      handleSaveEdit(desc.id);
                                    }
                                  }}
                                />
                                <button
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleSaveEdit(desc.id, desc.completed);
                                  }}
                                  className="ml-2 p-2 bg-gradient-to-r from-green-500 to-emerald-500 
                                       rounded-md text-white shadow-md shadow-green-500/30
                                       hover:shadow-green-500/50 transition-all duration-300"
                                  title="Save"
                                >
                                  <FaSave className="text-base" />
                                </button>
                              </>
                            ) : (
                              <>
                                <div className="flex items-center gap-x-3 flex-1">
                                  <div className="text-base">
                                    {desc.completed ? (
                                      <FaRegCircleCheck
                                        className="text-green-400 hover:text-green-300 transition-colors duration-300 cursor-pointer"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleToggleCompletion(
                                            desc.id,
                                            false,
                                            desc.text
                                          );
                                        }}
                                      />
                                    ) : (
                                      <FaRegCircle
                                        className="text-gray-400 hover:text-gray-300 transition-colors duration-300 cursor-pointer"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleToggleCompletion(
                                            desc.id,
                                            true,
                                            desc.text
                                          );
                                        }}
                                      />
                                    )}
                                  </div>
                                  <span
                                    className={`text-sm ${
                                      desc.completed
                                        ? "line-through text-gray-400"
                                        : "text-white"
                                    }`}
                                  >
                                    {desc.text}
                                  </span>
                                </div>
                                <button
                                  className="md:hidden text-gray-400 hover:text-white bg-transparent border-none outline-none ring-0  transition-colors duration-300"
                                  onClick={(e) => handleIconClick(e, desc.id)}
                                  style={{
                                    border: "none !important",
                                    outline: "0px !important",
                                    zIndex: "1000 !important",
                                  }}
                                >
                                  <FiMoreVertical />
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
          {currentPageTodo.filter((desc) => desc.completed).length > 0 && (
            <h6 className="text-gray-400 font-medium text-start mt-6 mb-4">
              Completed
            </h6>
          )}
          <Droppable droppableId="completed-todo-list" isDropDisabled={true}>
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="todo-list fancy-scrollbar"
              >
                {currentPageTodo.length > 0 &&
                  currentPageTodo
                    .filter((desc) => desc.completed)
                    .map((desc, index) => (
                      <div
                        key={desc.id}
                        className={`p-4 mb-3 rounded-lg flex justify-between items-center
                        ${
                          isSelected(desc.id)
                            ? "bg-gradient-to-r from-blue-500/20 to-cyan-500/20 "
                            : "bg-gray-900/50 hover:bg-gray-700/50"
                        } 
                        backdrop-blur-sm transition-all duration-300 cursor-pointer
                        shadow-md hover:shadow-lg`}
                        onClick={(e) =>
                          handleSelectDescription(desc.id, index, e)
                        }
                        onDoubleClick={() => handleDoubleClick(desc)}
                        onContextMenu={(e) => handleContextMenu(e, desc.id)}
                      >
                        {editingDescId === desc.id ? (
                          <>
                            <input
                              type="text"
                              value={editedDescription}
                              onChange={(e) =>
                                setEditedDescription(e.target.value)
                              }
                              className="flex-1 p-2 bg-gray-700 rounded-md text-white focus:outline-none 
                                       focus:ring-2 focus:ring-cyan-500 transition-all duration-300 text-sm"
                              ref={todoEditInputRef}
                              onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                  handleSaveEdit(desc.id);
                                }
                              }}
                            />
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                handleSaveEdit(desc.id, desc.completed);
                              }}
                              className="ml-2 p-2 bg-gradient-to-r from-green-500 to-emerald-500 
                                       rounded-md text-white shadow-md shadow-green-500/30
                                       hover:shadow-green-500/50 transition-all duration-300"
                              title="Save"
                            >
                              <FaSave className="text-base" />
                            </button>
                          </>
                        ) : (
                          <div className="flex items-center gap-x-3 flex-1">
                            <div className="text-base">
                              {desc.completed ? (
                                <FaRegCircleCheck
                                  className="text-green-400 hover:text-green-300 transition-colors duration-300 cursor-pointer"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleToggleCompletion(
                                      desc.id,
                                      false,
                                      desc.text
                                    );
                                  }}
                                />
                              ) : (
                                <FaRegCircle
                                  className="text-gray-400 hover:text-gray-300 transition-colors duration-300 cursor-pointer"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleToggleCompletion(
                                      desc.id,
                                      true,
                                      desc.text
                                    );
                                  }}
                                />
                              )}
                            </div>
                            <span
                              className={`text-sm ${
                                desc.completed
                                  ? "line-through text-gray-400"
                                  : "text-white"
                              }`}
                            >
                              {desc.text}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </div>
  );
}
